import { useEffect, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { StudentActiveFocus } from '../graphQLOperations/StudentTargetFocus';
import { StudentTargetFocusActiveQuery } from '../graphQLOperations/__generated__/StudentTargetFocusActiveQuery.graphql';
import { useAppDispatch } from '../redux/hooks';
import { ActiveStudentFocus, setActiveStudentFocus } from '../redux/reducers/focusSlice';

export interface CurrentTarget {
  id: string;
  name?: string | null;
  summary?: string | null;
}

interface Params {
  /** Units in the student's current roadmap (currentTargetGroupByStudentId.targetGroup.targets). */
  currentTargets: ReadonlyArray<CurrentTarget>;
  /** Bump to force a network revalidation (e.g. on homepage mount / access-token change). */
  fetchKey?: string | number;
}

/**
 * Reads the logged-in student's own active teacher-assigned focus (the resolver's
 * ownership filter scopes studentTargetFocuses to the caller), resolves it to a
 * unit within the already-loaded roadmap, and publishes it to focusSlice so the
 * modal and other student-experience components (US-8/US-9) can read it via
 * selectActiveFocusUnit. Returns null when there is no active focus, or when the
 * focused target is not in the student's roadmap (caller falls back to the default).
 */
export const useStudentActiveFocus = ({
  currentTargets,
  fetchKey,
}: Params): { activeStudentFocus: ActiveStudentFocus | null } => {
  const dispatch = useAppDispatch();

  const data = useLazyLoadQuery<StudentTargetFocusActiveQuery>(
    StudentActiveFocus,
    { filter: { isActive: 'ACTIVE' } },
    { fetchPolicy: 'store-and-network', fetchKey },
  );

  const row = (data.studentTargetFocuses ?? []).find(focus => focus?.isActive) ?? null;
  const rowId = row?.id ?? null;
  const rowSeenOn = row?.focusSeenOn ?? null;
  const rowOtherTargetsBlocked = row?.otherTargetsBlocked ?? false;
  const focusTargetId = row?.target?.id ?? null;

  // Resolve the focused target within the student's already-loaded roadmap units.
  const unit = focusTargetId ? currentTargets.find(target => target.id === focusTargetId) ?? null : null;
  const unitId = unit?.id ?? null;
  const unitName = unit?.name ?? null;
  const unitSummary = unit?.summary ?? undefined;

  // Depend on primitives (not the freshly-built currentTargets array or Relay
  // object refs) so the resolved focus keeps a stable identity across renders.
  const activeStudentFocus = useMemo<ActiveStudentFocus | null>(() => {
    if (!rowId || !unitId) return null;
    return {
      id: rowId,
      focusSeenOn: rowSeenOn,
      otherTargetsBlocked: rowOtherTargetsBlocked,
      unit: { id: unitId, name: unitName ?? '', summary: unitSummary },
    };
  }, [rowId, rowSeenOn, rowOtherTargetsBlocked, unitId, unitName, unitSummary]);

  useEffect(() => {
    dispatch(setActiveStudentFocus(activeStudentFocus));
  }, [dispatch, activeStudentFocus]);

  return { activeStudentFocus };
};
